/*
 * Description:
 * ---------------------
 * The Firewall screen's Overview: the AI analyst first (FirewallAnalyst), then
 * a drawing of what crosses this box (what the devices start goes out and its
 * replies come back; what nobody asked for stops at the box), then everything
 * the firewall does, a row each, with what it is doing on this box right now
 * and the section that changes it. Wording in firewallWords.js.
 *
 * ! file components/FirewallOverview.js
 */
import React from "react";
import { FiArrowRight } from "react-icons/fi";
import {
  TbArrowNarrowRight, TbBan, TbBuildingCommunity, TbDevices, TbGauge, TbKey, TbListCheck, TbShieldCheck, TbShieldOff,
  TbBug, TbNetwork, TbShieldLock, TbUsersGroup, TbWorld, TbWorldOff, TbRadar2,
} from "react-icons/tb";
import { IS_SINGLE_EDGE } from "../serverApi";
import { AiIcon } from "./AiIcon";
import FirewallAnalyst from "./FirewallAnalyst";
import * as W from "./firewallWords";

/* A node of the drawing: a disc with its icon, and its name under it. */
function Node({ x, Icon, title, sub, kind }) {
  return (
    <g className={`fw-node is-${kind}`}>
      <circle cx={x} cy="92" r="42" />
      <g transform={`translate(${x - 18} 74)`}><Icon size={36} strokeWidth={1.6} /></g>
      <text x={x} y="160" textAnchor="middle" className="fw-node-title">{title}</text>
      <text x={x} y="180" textAnchor="middle" className="fw-node-sub">{sub}</text>
    </g>
  );
}

function Head({ id, kind }) {
  return (
    <marker id={id} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
      <path d="M0,0 L10,5 L0,10 z" className={`fw-head-${kind}`} />
    </marker>
  );
}

/* What crosses the box, both ways. With the firewall on, the uninvited lane
   ends at the box; off, it runs on into the network. */
function Flow({ view }) {
  const f = W.flowWords(view);
  const on = !!view.on;
  return (
    <svg className={`fw-flow ${on ? "is-on" : "is-off"}`} viewBox="0 36 900 156" role="img" aria-label={f.aria}>
      <defs>
        <linearGradient id="fw-g-on" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#6366f1" /><stop offset="1" stopColor="#10b981" />
        </linearGradient>
        <linearGradient id="fw-g-off" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f59e0b" /><stop offset="1" stopColor="#f43f5e" />
        </linearGradient>
        <Head id="fw-head-ok" kind="ok" />
        <Head id="fw-head-in" kind="in" />
        <Head id="fw-head-open" kind="open" />
      </defs>
      {/* Each label sits where its traffic starts. */}
      <line x1="156" y1="80" x2="404" y2="80" className="fw-lane is-ok" />
      <line x1="496" y1="80" x2="744" y2="80" className="fw-lane is-ok" markerEnd="url(#fw-head-ok)" />
      <text x="280" textAnchor="middle" className="fw-lane-text is-ok">
        <tspan x="280" y="50" className="fw-lane-who">{f.out}</tspan>
        <tspan x="280" y="67">{f.outVerdict}</tspan>
      </text>
      {on ? (
        <>
          <line x1="744" y1="106" x2="530" y2="106" className="fw-lane is-in" markerEnd="url(#fw-head-in)" />
          <g transform="translate(497 95)" className="fw-ban"><circle cx="11" cy="11" r="13" /><TbBan size={22} strokeWidth={2} /></g>
        </>
      ) : (
        <line x1="744" y1="106" x2="158" y2="106" className="fw-lane is-open" markerEnd="url(#fw-head-open)" />
      )}
      <text x="620" textAnchor="middle" className={`fw-lane-text ${on ? "is-in" : "is-open"}`}>
        <tspan x="620" y="130" className="fw-lane-who">{f.in}</tspan>
        <tspan x="620" y="147">{f.inVerdict}</tspan>
      </text>
      <Node x={110} Icon={TbDevices} title={W.FLOW.lan} sub={W.FLOW.lanSub} kind="lan" />
      <Node x={450} Icon={on ? TbShieldCheck : TbShieldOff} title={W.FLOW.box} sub={f.boxSub} kind={on ? "box" : "box-off"} />
      <Node x={790} Icon={TbWorld} title={W.FLOW.internet} sub={W.FLOW.internetSub} kind="internet" />
    </svg>
  );
}

/* One thing the firewall does, as a row: what it is, what it is doing on this
   box now, and the section that changes it. Protection and smart controls
   share it. */
function Row({ Icon, words, now, live, action, onAction }) {
  return (
    <li className={`fw-protect is-${now.tone}`}>
      <span className="fw-protect-icon" aria-hidden="true"><Icon size={20} strokeWidth={1.8} /></span>
      <div className="fw-protect-main">
        <strong>{words.title}</strong>
        <p>{words.means}</p>
        {live && <p className="fw-protect-live">{live}</p>}
      </div>
      <div className="fw-protect-now">
        <strong>{now.state}</strong>
        {now.detail && <span>{now.detail}</span>}
        {action && <button type="button" className="fw-link" onClick={onAction}>{action} <FiArrowRight size={14} aria-hidden="true" /></button>}
      </div>
    </li>
  );
}

export default function FirewallOverview({ view, jailed, controls, learned, fleet, findings, rows, selectedNode, go, locked, onTurnOn, onBlockPlace }) {
  const profiles = controls?.profiles || [];
  const watchedOf = (id) => (learned?.groups || []).find((g) => g.group === id);
  const nameOf = (id) => profiles.find((p) => p.id === id)?.name || id;
  const on = !!view.on;
  const flow = W.flowWords(view);
  const watching = (learned?.groups || []).length > 0;
  const F = W.FEATURES;
  const compare = fleet.odd.length ? { tone: "warn", state: `${fleet.odd.length} to check` }
    : fleet.groups.length ? { tone: "on", state: "Comparing" } : { tone: "off", state: "Not set up" };
  return (
    <div className="fw-overview">
      <FirewallAnalyst selectedNode={selectedNode} findings={findings} rows={rows} locked={locked} onBlockPlace={onBlockPlace} />

      <section className="fw-card fw-flow-card">
        <h3 className="fw-card-title">How traffic crosses this box</h3>
        <Flow view={view} />
        <ul className="fw-flow-list">
          <li className="is-ok"><TbArrowNarrowRight size={18} aria-hidden="true" /> <span><strong>{flow.out}</strong>: {flow.outVerdict}</span></li>
          <li className={on ? "is-in" : "is-open"}><TbBan size={18} aria-hidden="true" /> <span><strong>{flow.in}</strong>: {flow.inVerdict}</span></li>
        </ul>
      </section>

      <section className="fw-card">
        <header className="fw-card-head is-split">
          <div>
            <h3 className="fw-card-title">{W.PROTECTION.title}</h3>
            <p>{on ? W.PROTECTION.on : W.PROTECTION.off}</p>
          </div>
          {!on && <button type="button" className="fw-btn is-primary" disabled={locked} onClick={onTurnOn}>{W.TURN_ON}</button>}
        </header>
        <ul className="fw-protects">
          <Row Icon={TbShieldCheck} words={F.strangers} now={W.protectNow("strangers", view)}
            action={F.strangers.action} onAction={() => go("activity")} />
          <Row Icon={TbKey} words={F.manage} now={W.protectNow("manage", view)}
            action={F.manage.action} onAction={() => go("rules", "in")} />
          {view.tunnel && <Row Icon={TbBuildingCommunity} words={F.apart} now={W.protectNow("apart", view)} />}
          <Row Icon={TbBug} words={F.lists} now={W.protectNow("lists", view)}
            action={view.blocklists ? "See the lists" : "Turn on"} onAction={() => go("rules")} />
          <Row Icon={TbWorldOff} words={F.countries} now={W.protectNow("countries", view)}
            action={(view.block_countries || []).length ? "Change" : "Pick countries"} onAction={() => go("rules")} />
          <Row Icon={TbGauge} words={F.flood} now={W.protectNow("flood", view)} />
          <Row Icon={TbShieldLock} words={F.bans} now={W.protectNow("bans", { ...view, jails: jailed })}
            action={F.bans.action} onAction={() => go("intrusions")} />
          <Row Icon={TbNetwork} words={F.shared} now={W.protectNow("shared", { ...view, jails: jailed })} />
        </ul>
      </section>

      <section className="fw-card">
        <header className="fw-card-head">
          <h3 className="fw-card-title">{W.SMART.title}</h3>
          <p>{W.SMART.sub}</p>
        </header>
        <ul className="fw-protects">
          <Row Icon={TbListCheck} words={F.rules} now={W.protectNow("rules", view)}
            action={(view.rules || []).length ? "See your rules" : "Write a rule"} onAction={() => go("rules")} />
          <Row Icon={TbUsersGroup} words={F.groups}
            now={profiles.length ? { tone: "on", state: W.plural(profiles.length, "group") } : { tone: "off", state: "None yet" }}
            live={W.groupsLive(profiles, watchedOf)} action={profiles.length ? "Manage groups" : "Create a group"} onAction={() => go("groups")} />
          <Row Icon={AiIcon} words={F.learn}
            now={watching ? { tone: "ai", state: "Learning" } : { tone: "off", state: "Off" }}
            live={W.learnLive(learned, nameOf)} action={watching ? "See what it learned" : "Start learning"} onAction={() => go("groups")} />
          {!IS_SINGLE_EDGE && fleet.available !== false && (
            <Row Icon={TbRadar2} words={F.compare} now={compare}
              live={W.compareLive(fleet)} action={F.compare.action} onAction={() => go("across")} />
          )}
        </ul>
      </section>

      <p className="fw-foot">{W.DOES_NOT}</p>
    </div>
  );
}
